import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { initiateSSOToApp } from "src/utils/sso";
import LoadingScreen from "./LoadingScreen";

// Apertura a tutti (07/09/2026): le pagine utente del pannello (/user/*) vivono
// nella Summus App (app.summus.life/network, anche da PC). Il pannello resta
// solo per l'admin. Chi arriva qui con un vecchio link o un segnalibro viene
// portato via SSO alla pagina corrispondente dell'app, senza rifare il login.
//
// Interruttore: REACT_APP_USER_PANEL_OPEN=1 (build-time, come tutte le
// REACT_APP_*) disattiva il rimando e mostra di nuovo il pannello — rollback
// senza toccare codice. Vuota o assente = rimando attivo.
//
// L'admin non passa mai di qui: UserGuard lo manda su /admin prima.

const PANEL_OPEN = process.env.REACT_APP_USER_PANEL_OPEN === "1";

// Prefissi del pannello (senza /user) → pagina dell'app. Ordine: dal più
// specifico al più generico, vince la prima corrispondenza. Le pagine orfane
// o morte (business-builder, blogs, income-report, help-center, ...) cadono
// sulla Panoramica /network: nell'app non hanno un equivalente.
const MAP = [
  ["/online-store/my-orders", (rest) => (rest ? `/network/orders/${rest}` : "/network/orders")],
  ["/online-store", () => "/products"],
  ["/subscriptions", () => "/network/purchases"],
  ["/financial/payout", () => "/network/payout"],
  ["/financial", () => "/network/e-wallet"],
  ["/genealogy", () => "/network/tree"],
  ["/profile/settings", () => "/network/profile/settings"],
  ["/profile/referrals", () => "/network/profile/referrals"],
  ["/profile/edit", () => "/network/profile/edit"],
  ["/profile/wallets", () => "/network/profile/wallets"],
  ["/profile", () => "/network/profile"],
  ["/training", () => "/network/training"],
  ["/monthly-prizes", () => "/network/monthly-prizes"],
  ["/events", () => "/network/events"],
  ["/help-center", () => "/dashboard"],
];

export function mapPanelPathToApp(pathname) {
  const sub = String(pathname || "").replace(/^\/user/, "") || "/";
  for (const [prefix, build] of MAP) {
    if (sub === prefix || sub.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`)) {
      const rest = sub.slice(prefix.length).replace(/^\//, "");
      // Solo segmenti semplici (id numerici o slug): mai inoltrare query o
      // pezzi di path arbitrari verso l'app.
      const safeRest = /^[A-Za-z0-9_-]+$/.test(rest) ? rest : "";
      return build(safeRest);
    }
  }
  return "/network";
}

export default function PanelToAppRedirect({ children }) {
  const location = useLocation();
  const started = useRef(false);

  useEffect(() => {
    if (PANEL_OPEN || started.current) return;
    started.current = true;
    const appUrl = (
      process.env.REACT_APP_APP_URL || "http://app.summus_network.localhost"
    ).replace(/\/$/, "");
    initiateSSOToApp(`${appUrl}${mapPanelPathToApp(location.pathname)}`);
  }, [location.pathname]);

  if (PANEL_OPEN) return <>{children}</>;
  return <LoadingScreen />;
}

PanelToAppRedirect.propTypes = {
  children: PropTypes.node,
};
